import { fetchJSON as fetchAPI } from './client';

export type ExcellenceDashboardSnapshot = {
  tenant_id: string; excellence_score: number; code_quality_score: number;
  architecture_score: number; reliability_score: number; performance_score: number;
  test_coverage_pct: number; open_debt_items: number; critical_violations: number; generated_at: string;
};

export type CodeQualitySnapshot = {
  static_analysis_pass: boolean; lint_violations: number; complexity_breaches: number;
  security_findings: number; dead_code_pct: number; format_compliance_pct: number;
};

export type ArchitectureComplianceSnapshot = {
  repo_structure_valid: boolean; layer_isolation_ok: boolean; dependency_violations: number;
  adr_compliance_pct: number; contract_violations: number;
};

export type TechnicalDebtItem = {
  id: string; title: string; priority: string; owner: string;
  estimated_cost: string; business_risk: string; status: string;
};

export type ReliabilitySnapshot = {
  availability_pct: number; error_rate_pct: number; mttd_minutes: number; mttr_minutes: number;
  incident_count_30d: number; deployment_success_pct: number; rollback_count_30d: number;
};

export type PerformanceBudgetEntry = {
  key: string; target: string; current: string; status: string; p95_ms?: number;
};

export type DeveloperExperienceSnapshot = {
  build_time_seconds: number; test_time_seconds: number; local_setup_minutes: number;
  hot_reload_enabled: boolean; doc_coverage_pct: number;
};

export type ObservabilitySnapshot = {
  metrics_coverage_pct: number; tracing_coverage_pct: number; structured_logging_pct: number;
  slo_compliance_pct: number; active_alerts: number;
};

export type TestingStandardsSnapshot = {
  unit_coverage_pct: number; integration_coverage_pct: number; contract_tests_pass: boolean;
  e2e_tests_pass: boolean; load_tests_pass: boolean; security_tests_pass: boolean; a11y_tests_pass: boolean;
};

export type ReleaseEngineeringSnapshot = {
  branch_strategy: string; release_cadence: string; canary_enabled: boolean;
  feature_flags_active: number; last_release_status: string; rollback_ready: boolean;
};

export type ImprovementCycle = {
  id: string; area: string; action: string; impact: string; status: string; updated_at: string;
};

const COMMAND_BASE = import.meta.env.VITE_COMMANDCENTER_API ?? '/api/v1/command';
const fetchC = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const engineeringExcellenceApi = {
  dashboard: () => fetchC<ExcellenceDashboardSnapshot>('/engineering-excellence/dashboard'),
  codeQuality: () => fetchC<CodeQualitySnapshot>('/engineering-excellence/code-quality'),
  architecture: () => fetchC<ArchitectureComplianceSnapshot>('/engineering-excellence/architecture'),
  techDebt: () => fetchC<{ items: TechnicalDebtItem[] }>('/engineering-excellence/tech-debt').then((r) => r.items),
  reliability: () => fetchC<ReliabilitySnapshot>('/engineering-excellence/reliability'),
  performance: () => fetchC<{ budgets: PerformanceBudgetEntry[] }>('/engineering-excellence/performance').then((r) => r.budgets),
  devex: () => fetchC<DeveloperExperienceSnapshot>('/engineering-excellence/devex'),
  observability: () => fetchC<ObservabilitySnapshot>('/engineering-excellence/observability'),
  testing: () => fetchC<TestingStandardsSnapshot>('/engineering-excellence/testing'),
  release: () => fetchC<ReleaseEngineeringSnapshot>('/engineering-excellence/release'),
  improvement: () => fetchC<{ cycles: ImprovementCycle[] }>('/engineering-excellence/improvement').then((r) => r.cycles),
};
